import { useFormikContext } from 'formik';
import { useParams } from 'react-router-dom';

import ruleApi from '@src/features/programs/pages/create/services/rule-api';
import stageApi from '@src/features/programs/pages/create/services/stage-api';
import { REMINDER_TYPES } from '@src/shared/types/reminder';
import { RULE_TYPES } from '@src/shared/types/rule';
import { DateRuleFields } from '@src/shared/features/reminders/dialogs/create-or-edit-reminder/date-rule-fields';
import { OtherRelatedFields } from '@src/shared/features/reminders/dialogs/create-or-edit-reminder/other-related-fields';
import { ProgramRelatedFields } from '@src/shared/features/reminders/dialogs/create-or-edit-reminder/program-related-fields';
import { ProgramStageMultiSelect } from '@src/shared/features/reminders/dialogs/create-or-edit-reminder/program-stage-multi-select';

import { CreateReminderScheduleSection } from '../create-reminder-schedule-section';
import type { TCreateReminderFormValues } from '../schema';
import { StageReminderTypeSelect } from './stage-reminder-type-select';
import { FlightInfoFields } from '@src/shared/features/reminders/dialogs/create-or-edit-reminder/flight-info-fields';
import { SubmissionDateFields } from '@src/shared/features/reminders/dialogs/create-or-edit-reminder/submission-date-fields';

export function StageReminderDetailsSection() {
  const { values } = useFormikContext<TCreateReminderFormValues>();
  const { id: programId = '' } = useParams();

  const { data: stagesData } = stageApi.useFetch({ program: programId });
  const stages = stagesData?.results || [];

  const { data: dateRulesData } = ruleApi.useFetch({
    program: programId,
    type: [RULE_TYPES.date],
  });
  const dateRules = dateRulesData?.results || [];

  const { data: flightInfoRulesData } = ruleApi.useFetch({
    program: programId,
    type: [RULE_TYPES.flightInfo],
  });
  const flightInfoRules = flightInfoRulesData?.results || [];

  const allowedTypes = [
    REMINDER_TYPES.programDateRelated,
    REMINDER_TYPES.specificDate,
    REMINDER_TYPES.otherRules,
    REMINDER_TYPES.submissionDate,
    ...(dateRules.length ? [REMINDER_TYPES.dateRule] : []),
    ...(flightInfoRules.length ? [REMINDER_TYPES.flightInfo] : []),
  ];

  return (
    <section className='rounded-xl border border-[#E8E7F0] bg-white p-4'>
      <p className='mb-4 text-sm font-semibold text-gray-500'>
        3. Reminder Details
      </p>

      <div className='flex flex-col gap-4'>
        <StageReminderTypeSelect allowedTypes={allowedTypes} />

        {values.type === REMINDER_TYPES.programDateRelated && (
          <ProgramRelatedFields />
        )}

        {values.type === REMINDER_TYPES.otherRules && (
          <OtherRelatedFields hideRepetition />
        )}

        {values.type === REMINDER_TYPES.dateRule && <DateRuleFields />}

        {values.type === REMINDER_TYPES.specificDate && (
          <div className='flex flex-col gap-4'>
            <ProgramStageMultiSelect stages={stages} />
            <CreateReminderScheduleSection embedded />
          </div>
        )}

        {values.type === REMINDER_TYPES.flightInfo && <FlightInfoFields />}

        {values.type === REMINDER_TYPES.submissionDate && (
          <SubmissionDateFields />
        )}
      </div>
    </section>
  );
}
